import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
  ListFilter,
  EyeOff,
  Copy,
  MoreVertical,
  Search,
  X,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MarkdownContent } from '../../ui/markdown-content';
import { SparklineChart } from '../../ui/sparkline-chart';
import { Skeleton } from '../../ui/skeleton';
import { useVfsContent } from '@/hooks/useVfsContent';
import { useToast } from '../../ui/Toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../ui/dropdown-menu';
import { Checkbox } from '../../ui/checkbox';
import {
  sortRows,
  filterRows,
  rowsToCsv,
  parseTableData,
  getDistinctValues,
  coerceCellToString,
  escapeLeadingBlockMarkdown,
  type SortState,
  type TableRow,
  type TableColumn,
  type ValueFilters,
} from './AuiTable.helpers';
import {
  resolveColumnType,
  formatCellValue,
  type ColumnTypeId,
} from './AuiTable.format';
import type { AuiRendererProps } from '../registry';
import { useSurfaceNodeUiState } from '../AuiSurfaceContext';

type RawColumn = string | { header: string; key: string; type?: string };

/** Per-column resolved value type (null = no formatting), keyed by column key. */
type ColumnTypes = Map<string, ColumnTypeId | null>;

export function AuiTable({ node }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const interactive = (props.interactive as boolean | undefined) ?? true;
  const rawColumns = useMemo(() => (props.columns as RawColumn[]) ?? [], [props.columns]);
  const inlineRows = useMemo(() => (props.rows as unknown[]) ?? [], [props.rows]);
  const rowsUrl = props.rowsUrl as string | undefined;
  const fetched = useVfsContent(rowsUrl);

  const { rows, parseError } = useMemo<{
    rows: TableRow[];
    parseError: string | null;
  }>(() => {
    if (!rowsUrl) return { rows: inlineRows as TableRow[], parseError: null };
    if (!fetched.data) return { rows: [], parseError: null };
    return parseTableData(fetched.data, rowsUrl);
  }, [rowsUrl, fetched.data, inlineRows]);

  const columns = useMemo<TableColumn[]>(
    () =>
      rawColumns.map((col) =>
        typeof col === 'string'
          ? { header: col, key: col }
          : { header: col.header, key: col.key, type: col.type },
      ),
    [rawColumns],
  );

  // Resolve each column's value type once: an explicit `type` hint wins, else
  // auto-detect from the column's raw values. Drives cell + filter formatting.
  const columnTypes = useMemo<ColumnTypes>(() => {
    const map: ColumnTypes = new Map();
    for (const col of columns) {
      const key = String(col.key);
      map.set(key, resolveColumnType(getDistinctValues(rows, key), col.type));
    }
    return map;
  }, [columns, rows]);

  if (rowsUrl && fetched.loading) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid="aui-table">
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (rowsUrl && (fetched.error || parseError)) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid="aui-table">
        <p className="text-sm text-destructive">
          Failed to load table data: {fetched.error?.message ?? parseError}
        </p>
      </div>
    );
  }

  if (!interactive) {
    return <StaticTable columns={columns} rows={rows} columnTypes={columnTypes} />;
  }

  return (
    <InteractiveTable
      nodeId={node.id}
      columns={columns}
      rows={rows}
      columnTypes={columnTypes}
    />
  );
}

function CellContent({
  cell,
  type,
  showRaw,
}: {
  cell: unknown;
  type: ColumnTypeId | null;
  showRaw: boolean;
}) {
  const spark =
    cell != null && typeof cell === 'object' && 'sparkline' in (cell as Record<string, unknown>)
      ? (cell as { sparkline: number[]; color?: number | string; variant?: 'line' | 'bar' })
      : null;
  if (spark) {
    return (
      <SparklineChart
        data={spark.sparkline}
        color={(spark.color as 1 | 2 | 3 | 4 | 5 | string) ?? 1}
        variant={spark.variant ?? 'line'}
        height={24}
        width={80}
        showArea
      />
    );
  }
  // Coerce with the same stringifier sort/filter/CSV use, so the cell, the
  // filter list and exports all agree on the underlying value.
  const raw = coerceCellToString(cell);
  const display = showRaw || !type ? raw : formatCellValue(raw, type);
  const isFormatted = display !== raw;
  return (
    // Title surfaces the source value (e.g. the ISO timestamp) on hover.
    <div title={isFormatted ? raw : undefined}>
      <MarkdownContent size="xs">{escapeLeadingBlockMarkdown(display)}</MarkdownContent>
    </div>
  );
}

function StaticTable({
  columns,
  rows,
  columnTypes,
}: {
  columns: TableColumn[];
  rows: TableRow[];
  columnTypes: ColumnTypes;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="aui-table">
      <table className="min-w-full divide-y divide-border">
        <thead className="bg-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={String(col.key)} className="px-4 py-2 text-sm text-foreground">
                  <CellContent
                    cell={(row as Record<string | number, unknown>)[col.key]}
                    type={columnTypes.get(String(col.key)) ?? null}
                    showRaw={false}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface PersistedTableSlice {
  sort: SortState;
  filters: Record<string, string[]>;
  hidden: string[];
  order: string[] | null;
  /** Column keys currently displaying raw (unformatted) values. */
  raw: string[];
}

const PERSISTED_TABLE_DEFAULT: PersistedTableSlice = {
  sort: null,
  filters: {},
  hidden: [],
  order: null,
  raw: [],
};

function InteractiveTable({
  nodeId,
  columns,
  rows,
  columnTypes,
}: {
  nodeId: string;
  columns: TableColumn[];
  rows: TableRow[];
  columnTypes: ColumnTypes;
}) {
  const toast = useToast();
  const [persisted, setPersisted] = useSurfaceNodeUiState<'table', PersistedTableSlice>(
    nodeId,
    'table',
    PERSISTED_TABLE_DEFAULT,
  );

  // Local mirror — Sets are reconstituted at the boundary so the existing
  // header/menu APIs (Set<string>) keep working unchanged.
  const [sort, setSort] = useState<SortState>(persisted.sort);
  const [filters, setFilters] = useState<ValueFilters>(() =>
    Object.fromEntries(
      Object.entries(persisted.filters).map(([k, v]) => [k, new Set(v)]),
    ),
  );
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(persisted.hidden));
  const [order, setOrder] = useState<string[] | null>(persisted.order);
  const [rawCols, setRawCols] = useState<Set<string>>(() => new Set(persisted.raw ?? []));

  // Persist when local state differs from the persisted slice. We compare
  // serialized blobs to avoid a redundant write on the very first render
  // (where local state was just seeded from `persisted`).
  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    const next: PersistedTableSlice = {
      sort,
      filters: Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, [...v]]),
      ),
      hidden: [...hidden],
      order,
      raw: [...rawCols],
    };
    const serialized = JSON.stringify(next);
    if (lastSentRef.current === null) {
      // First render: seed without dispatching.
      lastSentRef.current = JSON.stringify(persisted);
    }
    if (serialized === lastSentRef.current) return;
    lastSentRef.current = serialized;
    setPersisted(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, filters, hidden, order, rawCols]);

  const knownKeys = useMemo(() => new Set(columns.map((c) => String(c.key))), [columns]);

  // Self-correct stale persisted entries when the spec's columns change
  // (agent renamed/removed columns). Sort already self-corrects below.
  useEffect(() => {
    setHidden((curr) => {
      const filtered = new Set([...curr].filter((k) => knownKeys.has(k)));
      return filtered.size === curr.size ? curr : filtered;
    });
    setFilters((curr) => {
      const next: ValueFilters = {};
      let changed = false;
      for (const [k, v] of Object.entries(curr)) {
        if (knownKeys.has(k)) next[k] = v;
        else changed = true;
      }
      return changed ? next : curr;
    });
    setOrder((curr) => {
      if (!curr) return curr;
      const filtered = curr.filter((k) => knownKeys.has(k));
      return filtered.length === curr.length ? curr : filtered;
    });
    setRawCols((curr) => {
      const filtered = new Set([...curr].filter((k) => knownKeys.has(k)));
      return filtered.size === curr.size ? curr : filtered;
    });
  }, [knownKeys]);

  const visibleColumns = useMemo<TableColumn[]>(() => {
    const specOrder = columns.map((c) => String(c.key));
    const orderedKeys = order
      ? [
          ...order.filter((k) => knownKeys.has(k)),
          ...specOrder.filter((k) => !order.includes(k)),
        ]
      : specOrder;
    const byKey = new Map(columns.map((c) => [String(c.key), c]));
    return orderedKeys
      .filter((k) => !hidden.has(k))
      .map((k) => byKey.get(k))
      .filter((c): c is TableColumn => c !== undefined);
  }, [columns, order, hidden, knownKeys]);

  useEffect(() => {
    if (sort && !knownKeys.has(String(sort.key))) setSort(null);
  }, [sort, knownKeys]);

  const visibleRows = useMemo(
    () => sortRows(filterRows(rows, filters), sort),
    [rows, filters, sort],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visibleKeys = visibleColumns.map((c) => String(c.key));
    const oldIdx = visibleKeys.indexOf(String(active.id));
    const newIdx = visibleKeys.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    const reorderedVisible = arrayMove(visibleKeys, oldIdx, newIdx);
    const hiddenInSpec = columns
      .map((c) => String(c.key))
      .filter((k) => hidden.has(k));
    setOrder([...reorderedVisible, ...hiddenInSpec]);
  };

  const cycleSort = (key: string) =>
    setSort((curr) => {
      if (!curr || String(curr.key) !== key) return { key, dir: 'asc' };
      if (curr.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });

  const setColumnSort = (key: string, dir: 'asc' | 'desc' | null) =>
    setSort(dir ? { key, dir } : null);

  const setFilterExcluded = (key: string, excluded: Set<string>) =>
    setFilters((curr) => {
      const next = { ...curr };
      if (excluded.size === 0) delete next[key];
      else next[key] = excluded;
      return next;
    });

  const toggleHidden = (key: string) =>
    setHidden((curr) => {
      const next = new Set(curr);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleRaw = (key: string) =>
    setRawCols((curr) => {
      const next = new Set(curr);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const showAllColumns = () => setHidden(new Set());
  const hideAllColumns = () =>
    setHidden(new Set(columns.slice(1).map((c) => String(c.key))));

  const handleCopyCsv = async () => {
    const csv = rowsToCsv(visibleRows, visibleColumns);
    try {
      await navigator.clipboard.writeText(csv);
      toast.success('Table copied as CSV');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const allHidden = visibleColumns.length === 0;
  const visibleKeys = visibleColumns.map((c) => String(c.key));

  return (
    <div data-testid="aui-table">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={visibleKeys} strategy={horizontalListSortingStrategy}>
                <tr>
                  {visibleColumns.map((col) => (
                    <SortableHeaderCell
                      key={String(col.key)}
                      col={col}
                      rows={rows}
                      columnType={columnTypes.get(String(col.key)) ?? null}
                      showRaw={rawCols.has(String(col.key))}
                      sortDir={
                        sort && String(sort.key) === String(col.key) ? sort.dir : null
                      }
                      excluded={filters[String(col.key)] ?? null}
                      canHide={visibleColumns.length > 1}
                      onCycleSort={() => cycleSort(String(col.key))}
                      onSetSort={(dir) => setColumnSort(String(col.key), dir)}
                      onSetExcluded={(set) => setFilterExcluded(String(col.key), set)}
                      onHide={() => toggleHidden(String(col.key))}
                      onToggleRaw={() => toggleRaw(String(col.key))}
                    />
                  ))}
                  <th className="w-8 px-1 py-2 align-middle">
                    <TableActionsMenu
                      columns={columns}
                      hidden={hidden}
                      canCopy={!allHidden}
                      onToggle={toggleHidden}
                      onShowAll={showAllColumns}
                      onHideAll={hideAllColumns}
                      onCopyCsv={handleCopyCsv}
                    />
                  </th>
                </tr>
              </SortableContext>
            </DndContext>
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {allHidden ? (
              <tr>
                <td
                  colSpan={2}
                  className="px-4 py-6 text-center text-sm text-muted-foreground"
                >
                  All columns hidden — use the menu to bring some back.
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 1}
                  className="px-4 py-6 text-center text-sm text-muted-foreground"
                >
                  No rows match the current filters.
                </td>
              </tr>
            ) : (
              visibleRows.map((row, i) => (
                <tr key={i}>
                  {visibleColumns.map((col) => (
                    <td
                      key={String(col.key)}
                      className="px-4 py-2 text-sm text-foreground"
                    >
                      <CellContent
                        cell={(row as Record<string | number, unknown>)[col.key]}
                        type={columnTypes.get(String(col.key)) ?? null}
                        showRaw={rawCols.has(String(col.key))}
                      />
                    </td>
                  ))}
                  <td className="w-8" aria-hidden="true" />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface SortableHeaderCellProps {
  col: TableColumn;
  rows: TableRow[];
  columnType: ColumnTypeId | null;
  showRaw: boolean;
  sortDir: 'asc' | 'desc' | null;
  excluded: Set<string> | null;
  canHide: boolean;
  onCycleSort: () => void;
  onSetSort: (dir: 'asc' | 'desc' | null) => void;
  onSetExcluded: (excluded: Set<string>) => void;
  onHide: () => void;
  onToggleRaw: () => void;
}

function SortableHeaderCell({
  col,
  rows,
  columnType,
  showRaw,
  sortDir,
  excluded,
  canHide,
  onCycleSort,
  onSetSort,
  onSetExcluded,
  onHide,
  onToggleRaw,
}: SortableHeaderCellProps) {
  const id = String(col.key);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const hasFilter = (excluded?.size ?? 0) > 0;
  const ariaSort: React.AriaAttributes['aria-sort'] =
    sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';

  return (
    <th
      ref={setNodeRef}
      style={style}
      role="columnheader"
      aria-sort={ariaSort}
      className="group/th relative px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground"
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onCycleSort}
          aria-label={`Sort by ${col.header}`}
          className="focus-ring -mx-1 inline-flex flex-1 cursor-pointer items-center gap-1 rounded px-1 py-0.5 uppercase hover:text-foreground focus-visible:ring-offset-muted"
          {...attributes}
          {...listeners}
        >
          <span className="truncate">{col.header}</span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={
                hasFilter
                  ? `${col.header} filter active`
                  : sortDir
                  ? `${col.header} sorted ${sortDir === 'asc' ? 'ascending' : 'descending'}`
                  : `${col.header} options`
              }
              onPointerDown={(e) => e.stopPropagation()}
              className={`focus-ring rounded p-0.5 transition-opacity hover:bg-accent hover:text-foreground focus-visible:ring-offset-muted focus:opacity-100 data-[state=open]:opacity-100 ${
                hasFilter || sortDir
                  ? 'opacity-100 text-foreground'
                  : 'opacity-0 group-hover/th:opacity-100'
              }`}
            >
              {sortDir === 'asc' ? (
                <ArrowUpNarrowWide className="h-3.5 w-3.5" />
              ) : sortDir === 'desc' ? (
                <ArrowDownWideNarrow className="h-3.5 w-3.5" />
              ) : (
                <ListFilter className="h-3.5 w-3.5" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72 p-0">
            <div className="p-1">
              <DropdownMenuItem onSelect={() => onSetSort('asc')}>
                <ArrowUpNarrowWide className="h-4 w-4 text-muted-foreground" />
                Sort A → Z
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onSetSort('desc')}>
                <ArrowDownWideNarrow className="h-4 w-4 text-muted-foreground" />
                Sort Z → A
              </DropdownMenuItem>
              {sortDir && (
                <DropdownMenuItem onSelect={() => onSetSort(null)}>
                  <X className="h-4 w-4 text-muted-foreground" />
                  Clear sort
                </DropdownMenuItem>
              )}
            </div>
            <DropdownMenuSeparator className="my-0" />
            <ValueFilterPanel
              rows={rows}
              columnKey={col.key}
              columnType={columnType}
              showRaw={showRaw}
              excluded={excluded ?? new Set<string>()}
              onSetExcluded={onSetExcluded}
            />
            {(columnType || canHide) && (
              <>
                <DropdownMenuSeparator className="my-0" />
                <div className="p-1">
                  {columnType && (
                    <DropdownMenuCheckboxItem
                      checked={showRaw}
                      onCheckedChange={onToggleRaw}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Show raw values
                    </DropdownMenuCheckboxItem>
                  )}
                  {canHide && (
                    <DropdownMenuItem onSelect={onHide}>
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                      Hide column
                    </DropdownMenuItem>
                  )}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </th>
  );
}

interface ValueFilterPanelProps {
  rows: TableRow[];
  columnKey: string | number;
  columnType: ColumnTypeId | null;
  showRaw: boolean;
  excluded: Set<string>;
  onSetExcluded: (excluded: Set<string>) => void;
}

function ValueFilterPanel({
  rows,
  columnKey,
  columnType,
  showRaw,
  excluded,
  onSetExcluded,
}: ValueFilterPanelProps) {
  const [search, setSearch] = useState('');
  const distinct = useMemo(() => getDistinctValues(rows, columnKey), [rows, columnKey]);
  // Values stay raw (the filter keys); only the displayed label is formatted,
  // matching the column's cells. Search runs against the visible label.
  const labelFor = (value: string) =>
    showRaw || !columnType ? value : formatCellValue(value, columnType);
  const matching = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return distinct;
    return distinct.filter((v) => labelFor(v).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinct, search, columnType, showRaw]);

  const matchingSelectedCount = matching.filter((v) => !excluded.has(v)).length;

  const toggle = (value: string) => {
    const next = new Set(excluded);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onSetExcluded(next);
  };

  const selectAllMatching = () => {
    const next = new Set(excluded);
    for (const v of matching) next.delete(v);
    onSetExcluded(next);
  };

  const clearAllMatching = () => {
    const next = new Set(excluded);
    for (const v of matching) next.add(v);
    onSetExcluded(next);
  };

  return (
    <div className="px-2 py-2 normal-case">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          placeholder="Search values"
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          className="focus-ring w-full rounded border border-border bg-background px-2 py-1 pl-7 pr-6 text-xs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Clear search"
            className="focus-ring absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={selectAllMatching}
            className="focus-ring rounded text-foreground/80 underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-40"
            disabled={matching.length === 0}
          >
            Select all
          </button>
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={clearAllMatching}
            className="focus-ring rounded text-foreground/80 underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-40"
            disabled={matching.length === 0}
          >
            Clear
          </button>
        </div>
        <span>
          {matchingSelectedCount}/{matching.length}
        </span>
      </div>
      <div
        className="mt-1 max-h-48 overflow-y-auto rounded border border-border bg-background"
        onWheel={(e) => e.stopPropagation()}
      >
        {matching.length === 0 ? (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No matching values
          </div>
        ) : (
          <ul className="py-1">
            {matching.map((value) => {
              const checked = !excluded.has(value);
              const display = value === '' ? '(Blank)' : labelFor(value);
              return (
                <li key={value}>
                  <label className="flex w-full cursor-pointer items-center gap-2 px-2 py-1 text-xs hover:bg-accent">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(value)}
                      className="h-3.5 w-3.5"
                    />
                    <span
                      className={`truncate ${value === '' ? 'italic text-muted-foreground' : ''}`}
                    >
                      {display}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

interface TableActionsMenuProps {
  columns: TableColumn[];
  hidden: Set<string>;
  canCopy: boolean;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onCopyCsv: () => void;
}

function TableActionsMenu({
  columns,
  hidden,
  canCopy,
  onToggle,
  onShowAll,
  onHideAll,
  onCopyCsv,
}: TableActionsMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Table options"
          title="Table options"
          className="focus-ring rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-offset-muted data-[state=open]:bg-accent data-[state=open]:text-foreground"
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[11px] font-normal uppercase tracking-wide text-muted-foreground">
          Columns
        </DropdownMenuLabel>
        {columns.map((col) => (
          <DropdownMenuCheckboxItem
            key={String(col.key)}
            checked={!hidden.has(String(col.key))}
            onCheckedChange={() => onToggle(String(col.key))}
            onSelect={(e) => e.preventDefault()}
          >
            {col.header}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onShowAll}>Show all columns</DropdownMenuItem>
        <DropdownMenuItem onSelect={onHideAll}>Hide all but first</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onCopyCsv} disabled={!canCopy}>
          <Copy className="h-4 w-4 text-muted-foreground" />
          Copy as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
